import React, { useEffect, useMemo, useState } from "react";
import type { StoredUploadState } from "../types/storedUpload";
import { multipartUploadViaWS, type UploadStartedInfo } from "../uploader";
import { useStore } from "../store";
import { getWs } from "../wsConnection";
import type { AppVersionMetadata } from "../types/appVersionMetadata";
import { logger } from '../log';
import styles from "./AddVersionDialog.module.css";

const log = logger('resume-upload');

interface ResumeUploadDialogProps {
  uploadState: StoredUploadState;
  onClose: () => void;
  onUploadStarted: (info: UploadStartedInfo) => void;
  onResumed: () => void;
}

export function ResumeUploadDialog({
  uploadState,
  onClose,
  onUploadStarted,
  onResumed
}: ResumeUploadDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string>("");
  const [metadata, setMetadata] = useState<AppVersionMetadata>(() => ({
    gameEngine: uploadState.gameEngine || "unreal",
    operatingSystem: uploadState.operatingSystem || "windows",
    executablePath: uploadState.executablePath || ""
  }));
  const expiresAt = useMemo(() => new Date(uploadState.expiresAt), [uploadState.expiresAt]);

  useEffect(() => {
    setMetadata(prev => ({
      ...prev,
      gameEngine: uploadState.gameEngine || prev.gameEngine,
      operatingSystem: uploadState.operatingSystem || prev.operatingSystem,
      executablePath: uploadState.executablePath || prev.executablePath
    }));
  }, [uploadState.gameEngine, uploadState.operatingSystem, uploadState.executablePath]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] || null;
    if (!selected) {
      setFile(null);
      return;
    }
    // Only check name and size (lastModified won't match since we don't store it)
    if (
      selected.name !== uploadState.file.name ||
      selected.size !== uploadState.file.size
    ) {
      setError("Selected file does not match the original upload (name or size mismatch).");
      setFile(null);
    } else {
      setError("");
      setFile(selected);
    }
  };

  const handleResume = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!file) {
      setError("Select the original file before resuming.");
      return;
    }

    if (!getWs()) {
      log.error('WebSocket not available for upload');
      setError("Connection unavailable. Please try again.");
      return;
    }

    let currentUploadId: string | undefined = uploadState.uploadId;
    const checkPaused = () => {
      if (!currentUploadId) return false;
      const currentState = useStore.getState();
      return currentState.uploadProgress[currentUploadId]?.paused === true;
    };

    // Start upload in background, don't wait for completion
    multipartUploadViaWS(
      file,
      (sentBytes, totalBytes) => {
        // Progress is tracked via upload.progress events in the store
        // This callback is mainly for local progress updates
      },
      uploadState.projectId,
      (info) => {
        currentUploadId = info.uploadId;
        onUploadStarted(info);
        // Close dialog immediately when upload starts
        onResumed();
        onClose();
      },
      uploadState.uploadId,
      metadata,
      uploadState.appName,
      uploadState.appId,
      uploadState.replaceVersionId,
      checkPaused
    ).catch(err => {
      if (err.message !== 'upload_paused') {
        log.error('upload failed', err);
        // Upload is already in background, just log the error
      }
    });
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 className={styles.modalTitle}>Resume Upload</h2>
        <p className={styles.modalSubtitle}>
          {uploadState.filename || uploadState.key.split('/').pop() || 'Upload'} • {((uploadState.totalBytes ?? uploadState.size ?? 0) / (1024 * 1024)).toFixed(2)} MB
        </p>
        <p className={styles.zipHelperText}>
          Expires {expiresAt.toLocaleString()}
        </p>

        <form className={styles.formGroup} onSubmit={handleResume}>
          {error && <div className={styles.zipError}>{error}</div>}

          <div className={styles.formGroup}>
            <label className={styles.label}>GAME ENGINE</label>
            <select
              className={styles.select}
              value={metadata.gameEngine}
              onChange={(e) => setMetadata(prev => ({ ...prev, gameEngine: e.target.value as AppVersionMetadata["gameEngine"] }))}
            >
              <option value="unreal">Unreal</option>
              <option value="unity">Unity</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>OPERATING SYSTEM</label>
            <select
              className={styles.select}
              value={metadata.operatingSystem}
              onChange={(e) => setMetadata(prev => ({ ...prev, operatingSystem: e.target.value as AppVersionMetadata["operatingSystem"] }))}
            >
              <option value="windows">Windows</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Build Archive</label>
            <input type="file" onChange={handleFileChange} className={styles.input} />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Executable Path *</label>
            <input
              type="text"
              className={styles.input}
              value={metadata.executablePath}
              required
              onChange={(e) =>
                setMetadata((prev) => ({ ...prev, executablePath: e.target.value }))
              }
            />
          </div>

          <div className={styles.buttonGroup}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonSecondary}`}
              onClick={onClose}
            >
              Cancel
            </button>
            <button type="submit" className={styles.button} disabled={!file}>
              Resume Upload
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
